import { memo, useCallback } from 'react';
import type { FormSearchPredicateData } from '@/services/api-types';
import type { FormFilterBaseProps } from './filter-base';

export interface NumberFilterProps extends FormFilterBaseProps {}

interface NumberRange {
  from: string;
  to: string;
}

/**
 * Number inputs keep their display state as strings so partial input (e.g.
 * "-", "1.") doesn't round-trip through Number() and get mangled. We coerce
 * to Number only at predicate emission time.
 */
function readRange(predicates: FormSearchPredicateData[]): NumberRange {
  const p = predicates[0];
  if (!p) return { from: '', to: '' };
  const asStr = (v: unknown) =>
    v === null || v === undefined || Number.isNaN(v as number) ? '' : String(v);
  if (p.op === 'BETWEEN') return { from: asStr(p.value), to: asStr(p.value2) };
  if (p.op === 'GTE') return { from: asStr(p.value), to: '' };
  if (p.op === 'LTE') return { from: '', to: asStr(p.value) };
  return { from: '', to: '' };
}

function toNumberOrNull(s: string): number | null {
  if (s === '') return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
}

function rangeToPredicates(
  fieldKey: string,
  from: string,
  to: string,
): FormSearchPredicateData[] {
  const fromN = toNumberOrNull(from);
  const toN = toNumberOrNull(to);
  if (fromN !== null && toN !== null) {
    return [{ fieldKey, op: 'BETWEEN', value: fromN, value2: toN }];
  }
  if (fromN !== null) return [{ fieldKey, op: 'GTE', value: fromN }];
  if (toN !== null) return [{ fieldKey, op: 'LTE', value: toN }];
  return [];
}

/**
 * Two number inputs (from/to) inside a single Records Search `.field` row.
 * Emits BETWEEN / GTE / LTE / [] depending on which sides are filled.
 */
export const NumberFilter = memo(function NumberFilter({
  field,
  predicates,
  onPredicatesChange,
}: NumberFilterProps) {
  const { from, to } = readRange(predicates);

  const handleFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onPredicatesChange(rangeToPredicates(field.key, e.target.value, to));
    },
    [field.key, to, onPredicatesChange],
  );
  const handleToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onPredicatesChange(rangeToPredicates(field.key, from, e.target.value));
    },
    [field.key, from, onPredicatesChange],
  );

  return (
    <div className="field">
      <label>{field.label}</label>
      <div className="form-filter-range">
        <input
          type="number"
          value={from}
          onChange={handleFromChange}
          aria-label={`${field.label} min`}
          placeholder="Min"
        />
        <span className="form-filter-range__sep">to</span>
        <input
          type="number"
          value={to}
          onChange={handleToChange}
          aria-label={`${field.label} max`}
          placeholder="Max"
        />
      </div>
    </div>
  );
});
